import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Wallet, Banknote, Calculator, CreditCard, ChevronRight, Loader2 } from 'lucide-react';
import { api } from '@/api/client';

export interface WalletSummary {
  scope: 'MERCHANT' | 'COMPANY';
  merchantId: number | null;
  /** The NET position: credit limit + wallet balance + COD we hold. All-time, never date-filtered. */
  shippingBalance: {
    walletBalance: number;
    creditLimit: number;
    rechargePaid: number;
    recharge: number;
    remittanceHeld: number;
    freight: number;
    available: number;
    blocked: boolean;
    shortfall: number;
    merchantCount?: number;
  };
  wallet: { balance: number; postpaidLimit: number; billingMode: string | null; merchantCount: number };
  remittance: { total: number; paid: number; pending: number; count: number };
  freight: { total: number; count: number };
  recharge: { total: number; online: number; manual: number; count: number; onlineCount: number; pending: number; pendingCount: number };
}

/** ₹ with thousands separators, no paise (header space is tight). */
function inr(v: number | undefined | null): string {
  return '₹' + Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
/** Compact form for the pill itself: ₹1.2L / ₹4.5Cr — full value stays in the panel. */
function inrCompact(v: number | undefined | null): string {
  const n = Number(v ?? 0);
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  return inr(n);
}

/**
 * Wallet pill in the app header.
 *
 * Shows the total wallet amount — the merchant's own balance in the merchant portal, the sum across
 * every live merchant for admins. Hovering (or clicking, for touch) opens the breakdown: total
 * remittance, total freight and total recharge. Each of those is a link to its full list.
 *
 * Silently renders nothing if the caller isn't allowed to read the summary, so the header degrades
 * cleanly for roles without finance access.
 */
export function WalletBalanceMenu({ portal }: { portal: 'admin' | 'merchant' }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, isError } = useQuery<WalletSummary | null>({
    queryKey: ['wallet-summary', portal],
    queryFn: async () => {
      try { return (await api.get<WalletSummary>('/wallet/summary')).data; }
      catch { return null; }        // 403 for roles without finance access → widget hides
    },
    // Wallet, remittance and freight all move during the day; refresh quietly.
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  // Hover with a small close delay so the pointer can travel from the pill into the panel.
  const hoverOpen = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const hoverClose = () => { closeTimer.current = setTimeout(() => setOpen(false), 180); };
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  if (isError || (!isLoading && !data)) return null;

  const isCompany = data?.scope === 'COMPANY';
  const go = (path: string) => { setOpen(false); nav(path); };

  // The headline figure is the NET position, not the raw `walletBalance` column. That column is only
  // ever credited (nothing debits it), so on its own it reads ₹0 for a merchant carrying lakhs of
  // unpaid freight — which is exactly the number nobody could act on.
  const sb = data?.shippingBalance;
  const net = sb?.available ?? 0;
  const isNegative = net < 0;

  const rows = [
    {
      key: 'remittance',
      label: 'Total Remittance',
      icon: <Banknote size={14} className="text-emerald-600" />,
      value: data?.remittance.total,
      hint: `${(data?.remittance.count ?? 0).toLocaleString()} COD shipments · ${inr(data?.remittance.pending)} still to pay`,
      to: '/remittance',
      cta: 'View all remittance',
    },
    {
      key: 'freight',
      label: 'Total Freight',
      icon: <Calculator size={14} className="text-indigo-600" />,
      value: data?.freight.total,
      hint: `${(data?.freight.count ?? 0).toLocaleString()} shipments billed`,
      to: '/freight',
      cta: 'View all freight',
    },
    {
      key: 'recharge',
      label: 'Recharge Amount',
      icon: <CreditCard size={14} className="text-violet-600" />,
      value: data?.recharge.total,
      hint: `${inr(data?.recharge.online)} via payment gateway · ${inr(data?.recharge.manual)} manual`,
      // Lands on the wallet page pre-filtered to gateway recharges.
      to: '/wallet?mode=online',
      cta: 'View gateway recharges',
    },
  ];

  return (
    <div ref={ref} className="relative" onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu" aria-expanded={open}
        title={isCompany
          ? 'Net across all merchants: wallet balance + COD held (freight is already deducted)'
          : 'Your balance: recharge + COD remittance we hold − freight billed'}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition ${
          isNegative
            ? 'border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800'
            : 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800'
        }`}>
        <Wallet size={14} />
        {isLoading
          ? <Loader2 size={12} className="animate-spin" />
          : <span className="font-mono">{inrCompact(net)}</span>}
        <span className="hidden lg:inline font-normal text-emerald-700/70">{isCompany ? 'wallet (all)' : 'wallet'}</span>
      </button>

      {open && data && (
        <div role="menu"
          className="absolute right-0 mt-2 w-[19rem] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Headline: the net position, with the arithmetic spelled out so the figure is auditable
              at a glance rather than being a number you have to trust. */}
          <div className={`px-4 py-3 border-b border-slate-100 bg-gradient-to-br ${isNegative ? 'from-rose-50' : 'from-emerald-50'} to-white`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              {isCompany ? `Net balance · ${sb?.merchantCount ?? data.wallet.merchantCount} merchants` : 'Wallet balance'}
            </div>
            <div className={`text-2xl font-bold font-mono ${isNegative ? 'text-rose-700' : 'text-slate-900'}`}>{inr(net)}</div>

            {/*
              The breakdown, stated as it actually works.
              
              It used to read `Recharge + COD held − Freight billed = Available`, and that equation was
              false in two ways at once. The first line is NOT recharges — it is `merchant.walletBalance`,
              which is recharges + opening balance MINUS freight already taken. Paid COD remittance is
              not in it: that went to the merchant's bank.
              And freight is NOT subtracted again — it is already inside that first figure, so deducting
              it a second time would understate every merchant by their whole freight bill.
              
              So the sum is credit limit (postpaid only) + wallet balance + COD held, and freight is
              shown below the total as context rather than as a pending deduction.
            */}
            {sb && (
              <div className="mt-2 space-y-0.5 text-[11px] font-mono">
                {/* POSTPAID only. A credit limit IS spending power on a credit account, so it is a
                    real term in the sum; a prepaid merchant has none and the row is not rendered. */}
                {sb.creditLimit > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span className="font-sans">Credit limit</span><span>+ {inr(sb.creditLimit)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600">
                  <span className="font-sans">Wallet balance</span><span>+ {inr(sb.walletBalance)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span className="font-sans">COD remittance held</span><span>+ {inr(sb.remittanceHeld)}</span>
                </div>
                <div className={`flex justify-between pt-1 border-t border-slate-200 font-semibold ${isNegative ? 'text-rose-700' : 'text-slate-900'}`}>
                  <span className="font-sans">Available</span><span>{inr(sb.available)}</span>
                </div>
                <div className="flex justify-between text-slate-400 pt-1">
                  <span className="font-sans">Freight billed to date</span><span>{inr(sb.freight)}</span>
                </div>
                <div className="text-[10px] text-slate-400 font-sans leading-snug">
                  Already deducted from the wallet balance above — shown for reference, not subtracted again.
                </div>
                {/* Answers the question the old "Recharge" label invited: how much has this merchant
                    actually paid in? Almost always nothing, and saying so plainly is the point. */}
                <div className="flex justify-between text-slate-400">
                  <span className="font-sans">Recharges paid in</span><span>{inr(sb.rechargePaid)}</span>
                </div>
              </div>
            )}

            {/* Informational ONLY. Shipping is not restricted on balance for prepaid or postpaid, so
                this states the position and stops short of implying a block or demanding a recharge. */}
            {isNegative && !isCompany && (
              <div className="text-[11px] text-rose-700 mt-1.5">
                The wallet balance plus COD held is short by {inr(sb?.shortfall)}.
              </div>
            )}
            {!isCompany && data.wallet.billingMode === 'POSTPAID' && (
              <div className="text-[11px] text-slate-500 mt-1">Postpaid · limit {inr(data.wallet.postpaidLimit)}</div>
            )}
            {data.recharge.pendingCount > 0 && (
              <div className="text-[11px] text-amber-700 mt-0.5">
                {inr(data.recharge.pending)} recharge pending ({data.recharge.pendingCount})
              </div>
            )}
          </div>

          {/* The three drill-downs */}
          <div className="py-1">
            {rows.map((r) => (
              <button key={r.key} type="button" onClick={() => go(r.to)} title={r.cta}
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 group">
                <span className="shrink-0">{r.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-700">{r.label}</span>
                    <span className="text-sm font-semibold font-mono text-slate-900">{inr(r.value)}</span>
                  </span>
                  <span className="block text-[11px] text-slate-400 truncate">{r.hint}</span>
                </span>
                <ChevronRight size={13} className="text-slate-300 group-hover:text-slate-500 shrink-0" />
              </button>
            ))}
          </div>

          <button type="button" onClick={() => go('/wallet')}
            className="w-full px-4 py-2 border-t border-slate-100 text-xs text-emerald-700 hover:bg-emerald-50 font-medium text-left">
            Open wallet &amp; transactions →
          </button>
        </div>
      )}
    </div>
  );
}
